/**
 * REDESIGN §3 — "מה קורה", the written summary at the very top of the home screen.
 *
 * Rules-based and deterministic: no model call, no cost, no network, no clock (the caller passes
 * `asOf`). Server-computed and delivered in `summary.narrative`, so the web app and any future
 * notification render the same text.
 *
 * Shape (§3): one headline plus 2–4 supporting sentences, each one fact with its number inline,
 * NEVER more than 4 sentences in total → headline + at most 3 lines. Selection order, personal:
 * coverage → biggest deviation → returns/failures → what is coming. Business: collection → VAT →
 * runway → missing invoices.
 *
 * Rules applied here:
 *  - Whole shekels, digits with thousands separators, dates `dd.mm` (`text.ts`).
 *  - Numbers are emitted as `num` PARTS so the web can wrap each one LTR-isolated; `text` is the
 *    same sentence flattened, for notifications.
 *  - "If a required input is missing (stale data, no baseline yet), DROP that sentence rather than
 *    hedging it." Every builder below returns `null` when an input it needs is absent — there is no
 *    partial template anywhere in this file.
 *  - "The block always renders at least a headline." With no data at all there is no figure to
 *    state, so `first_run` is the ONE sentence in the system without a number; it is the documented
 *    consequence of that rule (see `docs/insight-contract.md`).
 *  - Never "מצוין" / "כל הכבוד" — the vocabulary here is facts only.
 */
import { daysBetween } from './days.ts';
import { count, ddmm, inDaysPhrase, latePhrase, percent, ratio, shekels, shekelsAbs } from './text.ts';
import type { Agorot, DayString, SystemId } from './types.ts';

// ───────────────────────────── output ─────────────────────────────

/**
 * A run of a sentence. `num` runs are LTR tokens (`₪1,234`, `12.09`, `85%`, `1.9`, `3`) that the web
 * MUST render inside `dir="ltr"` (a `<bdi dir="ltr">`), or the shekel sign and the digits reorder
 * inside the RTL paragraph.
 */
export type NarrativePart = { kind: 'text'; text: string } | { kind: 'num'; text: string };

/** Every template in §3, plus the two documented additions (`first_run`, `quiet`). */
export const NARRATIVE_KEYS = [
  // personal headlines
  'p.headline.surplus',
  'p.headline.deficit',
  'p.headline.no_coverage',
  // personal lines
  'p.coverage_ok',
  'p.deviation',
  'p.returns',
  'p.coming',
  'p.quiet',
  // business
  'b.headline.profit',
  'b.collection',
  'b.vat',
  'b.runway',
  'b.missing_invoices',
  // both
  'first_run',
] as const;
export type NarrativeKey = (typeof NARRATIVE_KEYS)[number];

export interface NarrativeSentence {
  /** Which template produced it — the stable handle for tests, analytics and the web's React key. */
  key: NarrativeKey;
  /** The whole sentence as plain text (`parts` joined). What a notification would send. */
  text: string;
  parts: NarrativePart[];
}

export interface Narrative {
  system: SystemId;
  /** 18–20px, the only sentence at that size (§3). Always present. */
  headline: NarrativeSentence;
  /** 15px supporting sentences, 0–3 of them, already in §3's selection order. */
  lines: NarrativeSentence[];
}

/** §3: "Never more than 4 sentences" — the headline plus at most three lines. */
export const MAX_LINES = 3;

// ───────────────────────────── input ─────────────────────────────

/** The month's bank cash-flow hero (`in`/`out` are non-negative magnitudes, `net` is signed). */
export interface MonthFlowFacts {
  inAgorot: Agorot;
  outAgorot: Agorot;
  netAgorot: Agorot;
}

export interface CoverageFacts {
  /** Σ upcoming card charges in the next 30 days (non-negative). */
  chargesAgorot: Agorot;
  /** `forwardAvailable ?? booked` of the charged-from accounts (signed — an overdraft is negative). */
  availableAgorot: Agorot;
  /** Present ONLY when coverage is short. `date` is the day the money runs out. */
  shortfall: { gapAgorot: Agorot; date: DayString } | null;
  /** What is left after the charges (signed). Used by the "coverage ok" line only. */
  leftAgorot: Agorot;
}

export interface DeviationFacts {
  /** The category's Hebrew label, e.g. `מזון וסופר`. Never a group key. */
  label: string;
  amountAgorot: Agorot;
  baselineAgorot: Agorot;
  /** `mtd / baseline`. */
  ratio: number;
}

export interface ReturnsFacts {
  /** Number of returned standing orders / cheques in the asOf month (BRIEF §9.3). */
  count: number;
  /** Σ the fees they cost (non-negative). */
  feesAgorot: Agorot;
}

export interface NextEventFacts {
  /** In words: `חיוב כאל ****8993`, `החזר הלוואה`… */
  label: string;
  date: DayString;
}

export interface QuietFacts {
  /** Month spending as a percentage of the baseline, e.g. 85. */
  pctOfBaseline: number;
}

/** REDESIGN §3, personal. Any field may be `null`: its sentence is then dropped, never hedged. */
export interface PersonalNarrativeInput {
  system: 'personal';
  /** `null` = first run / empty database — no figures exist yet. */
  month: MonthFlowFacts | null;
  coverage: CoverageFacts | null;
  /** The biggest category over its baseline; `null` when there is no baseline yet or none is over. */
  deviation: DeviationFacts | null;
  /** `null` when returns could not be computed; `{count: 0}` is a POSITIVE statement of "none". */
  returns: ReturnsFacts | null;
  next: NextEventFacts | null;
  /** Supplied only when a baseline exists — otherwise the quiet-month sentence has no number. */
  quiet: QuietFacts | null;
}

export interface BusinessMonthFacts {
  inAgorot: Agorot;
  outAgorot: Agorot;
  /** Signed operating profit. */
  profitAgorot: Agorot;
}

export interface CollectionFacts {
  openAgorot: Agorot;
  overdueAgorot: Agorot;
  /** The largest overdue debtor. Without it the §3 template has no tail, so the line is dropped. */
  topDebtor: { name: string; amountAgorot: Agorot; days: number } | null;
}

export interface VatFacts {
  /** Display label of the bi-monthly period, e.g. `יולי–אוגוסט`. */
  periodLabel: string;
  dueDate: DayString;
  amountAgorot: Agorot;
}

export interface RunwayFacts {
  /** Forecast balance 30 days out (signed). */
  forecastAgorot: Agorot;
}

export interface MissingInvoiceFacts {
  count: number;
  /** Σ gross of the expenses with no tax invoice (non-negative). */
  amountAgorot: Agorot;
  /** The VAT that therefore cannot be offset (non-negative). */
  vatAgorot: Agorot;
}

/** REDESIGN §3, business. */
export interface BusinessNarrativeInput {
  system: 'business';
  month: BusinessMonthFacts | null;
  collection: CollectionFacts | null;
  vat: VatFacts | null;
  runway: RunwayFacts | null;
  missingInvoices: MissingInvoiceFacts | null;
}

export type NarrativeInput = PersonalNarrativeInput | BusinessNarrativeInput;

// ───────────────────────────── sentence builder ─────────────────────────────

interface SentenceBuilder {
  /** Hebrew prose. Adjacent runs are merged so the output has no empty or split text nodes. */
  t(text: string): SentenceBuilder;
  /** An LTR number token — the web renders this run isolated. */
  n(text: string): SentenceBuilder;
  /** `בעוד 4 ימים` · `מחר` · `היום` · `באיחור 3 ימים`, digits (when any) as their own token. */
  days(n: number): SentenceBuilder;
  /** A duration in days: `47 ימים` · `יומיים` · `יום אחד`. */
  dayCount(n: number): SentenceBuilder;
  done(key: NarrativeKey): NarrativeSentence;
}

function s(): SentenceBuilder {
  const parts: NarrativePart[] = [];
  const api: SentenceBuilder = {
    t(text) {
      if (text === '') return api;
      const last = parts[parts.length - 1];
      if (last && last.kind === 'text') last.text += text;
      else parts.push({ kind: 'text', text });
      return api;
    },
    n(text) {
      parts.push({ kind: 'num', text });
      return api;
    },
    days(n) {
      if (n < 0) {
        const k = -n;
        return k <= 2 ? api.t(latePhrase(k)) : api.t('באיחור ').n(count(k)).t(' ימים');
      }
      return n >= 3 ? api.t('בעוד ').n(count(n)).t(' ימים') : api.t(inDaysPhrase(n));
    },
    dayCount(n) {
      if (n === 1) return api.t('יום אחד');
      if (n === 2) return api.t('יומיים');
      return api.n(count(n)).t(' ימים');
    },
    done(key) {
      const snapshot = parts.map((p) => ({ ...p }));
      return { key, text: snapshot.map((p) => p.text).join(''), parts: snapshot };
    },
  };
  return api;
}

// ───────────────────────────── personal templates ─────────────────────────────

/** `החודש נכנס ₪{in} ויצא ₪{out} — אתה בעודף של ₪{net}.` */
function personalSurplus(m: MonthFlowFacts): NarrativeSentence {
  return s()
    .t('החודש נכנס ')
    .n(shekels(m.inAgorot))
    .t(' ויצא ')
    .n(shekels(m.outAgorot))
    .t(' — אתה בעודף של ')
    .n(shekelsAbs(m.netAgorot))
    .t('.')
    .done('p.headline.surplus');
}

/** `החודש יצא ₪{out} מול ₪{in} שנכנסו — פער של ₪{|net|}.` */
function personalDeficit(m: MonthFlowFacts): NarrativeSentence {
  return s()
    .t('החודש יצא ')
    .n(shekels(m.outAgorot))
    .t(' מול ')
    .n(shekels(m.inAgorot))
    .t(' שנכנסו — פער של ')
    .n(shekelsAbs(m.netAgorot))
    .t('.')
    .done('p.headline.deficit');
}

/** `חיובי האשראי הקרובים ₪{charges} גדולים מהיתרה הזמינה ₪{available} — חסרים ₪{gap} עד {date}.` */
function personalNoCoverage(c: CoverageFacts, shortfall: { gapAgorot: Agorot; date: DayString }): NarrativeSentence {
  return s()
    .t('חיובי האשראי הקרובים ')
    .n(shekels(c.chargesAgorot))
    .t(' גדולים מהיתרה הזמינה ')
    .n(shekels(c.availableAgorot))
    .t(' — חסרים ')
    .n(shekelsAbs(shortfall.gapAgorot))
    .t(' עד ')
    .n(ddmm(shortfall.date))
    .t('.')
    .done('p.headline.no_coverage');
}

/** `חיובי האשראי ב-30 הימים הקרובים ₪{charges} מכוסים; אחריהם יישארו ₪{left}.` */
export function personalCoverageOk(c: CoverageFacts): NarrativeSentence {
  return s()
    .t('חיובי האשראי ב-')
    .n('30')
    .t(' הימים הקרובים ')
    .n(shekels(c.chargesAgorot))
    .t(' מכוסים; אחריהם יישארו ')
    .n(shekels(c.leftAgorot))
    .t('.')
    .done('p.coverage_ok');
}

/** `החריגה הבולטת היא {category} — ₪{amount} מול ₪{baseline} בממוצע, פי {ratio}.` */
export function personalDeviation(d: DeviationFacts): NarrativeSentence {
  return s()
    .t('החריגה הבולטת היא ')
    .t(d.label)
    .t(' — ')
    .n(shekels(d.amountAgorot))
    .t(' מול ')
    .n(shekels(d.baselineAgorot))
    .t(' בממוצע, פי ')
    .n(ratio(d.ratio))
    .t('.')
    .done('p.deviation');
}

/**
 * `{n} הוראות קבע חזרו החודש ועלו ₪{fees} בעמלות.`
 * At `n === 1` the plural template is not Hebrew ("1 הוראות קבע חזרו"), so the singular is written
 * out; the sentence still carries a number (the fees), as §3 requires.
 */
function personalReturns(r: ReturnsFacts): NarrativeSentence {
  if (r.count === 1) {
    return s().t('הוראת קבע אחת חזרה החודש ועלתה ').n(shekels(r.feesAgorot)).t(' בעמלות.').done('p.returns');
  }
  return s()
    .n(count(r.count))
    .t(' הוראות קבע חזרו החודש ועלו ')
    .n(shekels(r.feesAgorot))
    .t(' בעמלות.')
    .done('p.returns');
}

/** `הדבר הקרוב ביותר: {label} ב-{date}, בעוד {n} ימים.` */
function personalComing(next: NextEventFacts, daysUntil: number): NarrativeSentence {
  return s()
    .t('הדבר הקרוב ביותר: ')
    .t(next.label)
    .t(' ב-')
    .n(ddmm(next.date))
    .t(', ')
    .days(daysUntil)
    .t('.')
    .done('p.coming');
}

/** `אין חריגות החודש. ההוצאות ב-{n}% מהממוצע ואין תשלומים שנכשלו.` */
function personalQuiet(q: QuietFacts): NarrativeSentence {
  return s()
    .t('אין חריגות החודש. ההוצאות ב-')
    .n(percent(q.pctOfBaseline))
    .t(' מהממוצע ואין תשלומים שנכשלו.')
    .done('p.quiet');
}

// ───────────────────────────── business templates ─────────────────────────────

/** `החודש נכנסו ₪{in} ויצאו ₪{out} — רווח תפעולי ₪{profit}.` */
function businessHeadline(m: BusinessMonthFacts): NarrativeSentence {
  return s()
    .t('החודש נכנסו ')
    .n(shekels(m.inAgorot))
    .t(' ויצאו ')
    .n(shekels(m.outAgorot))
    .t(' — רווח תפעולי ')
    .n(shekels(m.profitAgorot))
    .t('.')
    .done('b.headline.profit');
}

/** `לקוחות חייבים ₪{open}, מזה ₪{overdue} באיחור — הגדול ביותר {customer} ₪{amount}, {days} ימים.` */
function businessCollection(c: CollectionFacts, top: { name: string; amountAgorot: Agorot; days: number }): NarrativeSentence {
  return s()
    .t('לקוחות חייבים ')
    .n(shekels(c.openAgorot))
    .t(', מזה ')
    .n(shekels(c.overdueAgorot))
    .t(' באיחור — הגדול ביותר ')
    .t(top.name)
    .t(' ')
    .n(shekels(top.amountAgorot))
    .t(', ')
    .dayCount(top.days)
    .t('.')
    .done('b.collection');
}

/** `דיווח מע"מ ל-{period} עד {date}, בעוד {n} ימים — לתשלום כ-₪{amount}.` */
function businessVat(v: VatFacts, daysUntil: number): NarrativeSentence {
  return s()
    .t('דיווח מע"מ ל-')
    .t(v.periodLabel)
    .t(' עד ')
    .n(ddmm(v.dueDate))
    .t(', ')
    .days(daysUntil)
    .t(' — לתשלום כ-')
    .n(shekels(v.amountAgorot))
    .t('.')
    .done('b.vat');
}

/** `לפי הצפי, בעוד 30 יום היתרה תעמוד על ₪{forecast}.` */
export function businessRunway(r: RunwayFacts): NarrativeSentence {
  return s()
    .t('לפי הצפי, בעוד ')
    .n('30')
    .t(' יום היתרה תעמוד על ')
    .n(shekels(r.forecastAgorot))
    .t('.')
    .done('b.runway');
}

/**
 * `{n} הוצאות ללא חשבונית (₪{amount}) — מע"מ שלא ניתן לקזז ₪{vat}.`
 * §3 writes `({amount})` with no unit; the ₪ is added because §8 requires every number to carry
 * one. Singular at `n === 1`, same reason as `p.returns`.
 */
function businessMissingInvoices(m: MissingInvoiceFacts): NarrativeSentence {
  if (m.count === 1) {
    return s()
      .t('הוצאה אחת ללא חשבונית (')
      .n(shekels(m.amountAgorot))
      .t(') — מע"מ שלא ניתן לקזז ')
      .n(shekels(m.vatAgorot))
      .t('.')
      .done('b.missing_invoices');
  }
  return s()
    .n(count(m.count))
    .t(' הוצאות ללא חשבונית (')
    .n(shekels(m.amountAgorot))
    .t(') — מע"מ שלא ניתן לקזז ')
    .n(shekels(m.vatAgorot))
    .t('.')
    .done('b.missing_invoices');
}

/**
 * The only sentence without a number, and the only one §3 does not spell out. It exists because §3
 * also says "The block always renders at least a headline" and a first run has no figure to state;
 * hedging ("כנראה", "בערך") is forbidden, so the sentence states the fact and stops.
 */
function firstRun(): NarrativeSentence {
  return s().t('עדיין אין נתונים — אחרי הסנכרון הראשון יופיע כאן הסיכום.').done('first_run');
}

// ───────────────────────────── selection ─────────────────────────────

function daysUntil(day: DayString, asOf: DayString): number {
  return daysBetween(asOf, day);
}

function buildPersonal(input: PersonalNarrativeInput, asOf: DayString): Narrative {
  const { month, coverage, deviation, returns, next, quiet } = input;

  if (month === null) return { system: 'personal', headline: firstRun(), lines: [] };

  const shortfall = coverage?.shortfall ?? null;
  const headline =
    coverage !== null && shortfall !== null
      ? personalNoCoverage(coverage, shortfall)
      : month.netAgorot < 0
        ? personalDeficit(month)
        : personalSurplus(month);

  const lines: NarrativeSentence[] = [];
  const push = (sentence: NarrativeSentence | null): void => {
    if (sentence !== null && lines.length < MAX_LINES) lines.push(sentence);
  };

  // 1 · coverage — NOT as a supporting line. §3's own rule is "never a sentence that repeats what
  //     the tile beneath it already says verbatim", and §4.2 puts the answer directly under this
  //     block THREE ways: the hero (`נשאר אחרי החיובים ₪X`), its input line (`זמין ₪A · חיובים ₪B`)
  //     and the coverage chip. `p.coverage_ok` restated all three, so the same two figures printed
  //     twice 150px apart. A SHORTFALL is different — it is the headline above, where it is the
  //     only statement of the fact.
  //
  // 2 · the biggest deviation — same reason. The fact already appears as the `אפשר לחכות` row
  //     (`קניות וביגוד ₪2,210 מול בסיס ₪1,150`) and again in the categories strip block
  //     (`₪2,210 · חריגה ×1.9`). With NO deviation and a proven-quiet month the quiet sentence
  //     takes the slot — that one is stated nowhere else, so it stays.
  if (deviation === null && quiet !== null && returns !== null && returns.count === 0) push(personalQuiet(quiet));

  // 3 · returns / failures.
  push(returns !== null && returns.count > 0 ? personalReturns(returns) : null);

  // 4 · what is coming.
  push(next !== null ? personalComing(next, daysUntil(next.date, asOf)) : null);

  return { system: 'personal', headline, lines };
}

function buildBusiness(input: BusinessNarrativeInput, asOf: DayString): Narrative {
  const { month, collection, vat, runway, missingInvoices } = input;

  if (month === null) return { system: 'business', headline: firstRun(), lines: [] };

  const lines: NarrativeSentence[] = [];
  const push = (sentence: NarrativeSentence | null): void => {
    if (sentence !== null && lines.length < MAX_LINES) lines.push(sentence);
  };

  // 1 · collection. The §3 template ends with the largest debtor; without one there is no shorter
  // variant to fall back to, so the whole sentence is dropped rather than hedged.
  const top = collection?.topDebtor ?? null;
  push(collection !== null && top !== null && collection.openAgorot > 0 ? businessCollection(collection, top) : null);

  // 2 · VAT.
  push(vat !== null ? businessVat(vat, daysUntil(vat.dueDate, asOf)) : null);

  // 3 · runway — dropped for §3's "never a sentence that repeats what the tile beneath it already
  //     says verbatim": §5's hero sub-line already carries the 30-day forecast figure, and the
  //     delta chip beside it carries the direction.
  // (`businessRunway` stays exported for surfaces that have no hero — see below.)

  // 4 · missing invoices.
  push(missingInvoices !== null && missingInvoices.count > 0 ? businessMissingInvoices(missingInvoices) : null);

  return { system: 'business', headline: businessHeadline(month), lines };
}

/**
 * REDESIGN §3. Deterministic in `(input, asOf)` — no clock, no network, no model.
 *
 * @param asOf the Israel day of the freshness timestamp (ARCHITECTURE §3), used only for countdowns.
 */
export function buildNarrative(input: NarrativeInput, asOf: DayString): Narrative {
  return input.system === 'personal' ? buildPersonal(input, asOf) : buildBusiness(input, asOf);
}

/** Every sentence in reading order — the headline first. Handy for notifications and for tests. */
export function narrativeSentences(narrative: Narrative): NarrativeSentence[] {
  return [narrative.headline, ...narrative.lines];
}

/** The whole block as one plain-text paragraph. */
export function narrativeText(narrative: Narrative): string {
  return narrativeSentences(narrative)
    .map((sentence) => sentence.text)
    .join(' ');
}

/**
 * The three REDESIGN §3 templates that `buildNarrative` does NOT select for the home screen.
 *
 * They are correct sentences and they stay implemented and tested — §3 specifies them — but on the
 * §4 layout each one restates a figure the block immediately beneath it already shows (`p.coverage_ok`
 * → the hero, its input line and the coverage chip; `p.deviation` → the `אפשר לחכות` row and the
 * categories strip block; `b.runway` → the business hero's forecast sub-line), and §3 forbids exactly
 * that. A surface with no hero and no strip — a push notification, an email digest — should build
 * them explicitly.
 */
export const UNSELECTED_TEMPLATES = { personalCoverageOk, personalDeviation, businessRunway } as const;
